// This is a skeleton starter React component generated by Plasmic.
// This file is owned by you, feel free to edit as you see fit.
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { PlasmicDialog, DefaultDialogProps } from './plasmic/fragment_design_system/PlasmicDialog';
import SheetDialog, { type SheetDialogProps } from "@/common/components/atom/modal/sheetDialog";
import useResponsive from '@/common/hooks/useResponsive';

export interface DialogProps extends DefaultDialogProps {}

/**
 * Lazy until first opened (docs/performance.md §۳.۸).
 *
 * A search page mounts ~80 closed dialogs (4 per doctor card); each one ran PlasmicDialog's
 * state machinery plus the Radix Root/Trigger/Portal/Presence tree just to show a closed
 * trigger. Until the dialog is first opened we render only the trigger, exactly as Radix's
 * `DialogTrigger asChild` would (same Slot, same attributes, same click semantics), and
 * hand over to the real PlasmicDialog once `open` turns true. It then stays real, so close
 * animation, focus return and later opens are untouched.
 *
 * Only the safe case is lazy: `open` controlled by the parent (every Plasmic Studio usage
 * binds it to state) and a trigger that PlasmicDialog would pass through unchanged — a
 * single non-Fragment element (a Fragment would be wrapped in a div carrying the root
 * classes) — or no trigger at all. Anything else renders the real dialog as before.
 */
// Radix DialogTrigger's attributes while closed (`type` is valid on its Primitive.button,
// Slot's typings just don't list it).
const closedTriggerAttrs = { type: 'button', 'aria-haspopup': 'dialog', 'aria-expanded': false, 'data-state': 'closed' } as const;

function DesktopDialog(props: DialogProps) {
  const [activated, setActivated] = React.useState(false);
  const { open, onOpenChange, trigger, noTrigger } = props;
  const wantsReal = activated || open === undefined || !!open;

  React.useEffect(() => {
    if (open && !activated) setActivated(true);
  }, [open, activated]);

  if (wantsReal) return <PlasmicDialog {...props} />;

  if (noTrigger) return null;
  if (!React.isValidElement(trigger) || trigger.type === React.Fragment) return <PlasmicDialog {...props} />;

  // Same as Radix DialogTrigger (closed): attributes + composeEventHandlers(undefined, onOpenToggle),
  // which skips the toggle when the trigger's own onClick called preventDefault.
  return (
    <Slot
      {...closedTriggerAttrs}
      onClick={(event: React.MouseEvent) => {
        if (!event.defaultPrevented) onOpenChange?.(true);
      }}
    >
      {trigger}
    </Slot>
  );
}

/** Mobile: the site-wide bottom sheet (browser Back, `?sheets=` in the URL). Desktop: unchanged. */
function Dialog(props: DialogProps) {
  const { isMobile } = useResponsive();
  if (isMobile) return <SheetDialog {...(props as SheetDialogProps)} />;
  return <DesktopDialog {...props} />;
}

export default Dialog;
